/**
 * Utilidades centralizadas para manejo de fechas.
 * Elimina la duplicación de `new Date().toISOString()` en todo el codebase.
 */

import {
  JS_DAY_TO_WEEKDAY,
  type ParsedRecurrence,
  parseRecurrenceRule,
  WEEKDAYS_ORDER,
} from "../todos/scheduling";

const END_OF_DAY_HOURS = 23;
const END_OF_DAY_MINUTES = 59;
const END_OF_DAY_SECONDS = 59;
const END_OF_DAY_MS = 999;
const DATE_SLICE_LENGTH = 10;
const ONE_DAY = 1;
const ONE_WEEK = 7;
const ONE_MONTH = 1;
const ONE_YEAR = 1;

/**
 * Obtiene la fecha/hora actual en formato ISO string.
 * Reemplaza el patrón repetido: `new Date().toISOString()`
 */
export function nowISO(): string {
  return new Date().toISOString();
}

/**
 * Obtiene el final del día actual (23:59:59.999) en formato ISO string.
 */
export function endOfTodayISO(): string {
  const d = new Date();
  d.setHours(END_OF_DAY_HOURS, END_OF_DAY_MINUTES, END_OF_DAY_SECONDS, END_OF_DAY_MS);
  return d.toISOString();
}

/**
 * Obtiene solo la parte de fecha (YYYY-MM-DD) de la fecha actual.
 */
export function todayDateKey(): string {
  return new Date().toISOString().slice(0, DATE_SLICE_LENGTH);
}

/**
 * Calcula la próxima fecha de vencimiento basada en una fecha actual y una regla
 * de recurrencia (RRULE: FREQ, INTERVAL, BYDAY semanal, UNTIL). Usado para tareas
 * recurrentes. Devuelve `null` si la regla es inválida o si la siguiente
 * ocurrencia caería después de `UNTIL`.
 */
const DAYS_IN_WEEK = 7;

function advanceWeeklyByDays(base: Date, parsed: ParsedRecurrence): Date {
  // Índices (0=Lun … 6=Dom) de los días seleccionados, ordenados.
  const selected = parsed.byDays
    .map((day) => WEEKDAYS_ORDER.indexOf(day))
    .toSorted((a, b) => a - b);
  const baseWeekday = JS_DAY_TO_WEEKDAY[base.getDay()] ?? "MO";
  const baseIndex = WEEKDAYS_ORDER.indexOf(baseWeekday);

  const next = new Date(base);
  const laterThisWeek = selected.find((index) => index > baseIndex);
  if (laterThisWeek !== undefined) {
    next.setDate(next.getDate() + (laterThisWeek - baseIndex));
    return next;
  }
  // Salta al primer día seleccionado del bloque `interval` semanas adelante.
  const firstSelected = selected[0] ?? baseIndex;
  const daysToNextBlock =
    DAYS_IN_WEEK - baseIndex + firstSelected + (parsed.interval - 1) * DAYS_IN_WEEK;
  next.setDate(next.getDate() + daysToNextBlock);
  return next;
}

export function computeNextDueDate(
  currentDueDate: string | null | undefined,
  rule: string,
): string | null {
  const parsed = parseRecurrenceRule(rule);
  if (!parsed) {
    return null;
  }

  const base = currentDueDate ? new Date(currentDueDate) : new Date();
  let next = new Date(base);

  if (parsed.freq === "WEEKLY" && parsed.byDays.length > 0) {
    next = advanceWeeklyByDays(base, parsed);
  } else {
    switch (parsed.freq) {
      case "DAILY": {
        next.setDate(next.getDate() + ONE_DAY * parsed.interval);
        break;
      }
      case "WEEKLY": {
        next.setDate(next.getDate() + ONE_WEEK * parsed.interval);
        break;
      }
      case "MONTHLY": {
        next.setMonth(next.getMonth() + ONE_MONTH * parsed.interval);
        break;
      }
      case "YEARLY": {
        next.setFullYear(next.getFullYear() + ONE_YEAR * parsed.interval);
        break;
      }
      default: {
        return null;
      }
    }
  }

  // Respeta el fin de la recurrencia (UNTIL).
  if (parsed.until && next.getTime() > new Date(parsed.until).getTime()) {
    return null;
  }

  return next.toISOString();
}
